import { cn } from "@/lib/utils";
import { HTMLProps, forwardRef, useMemo } from "react";

export type BaseFormBoxProps = {
    style?: React.CSSProperties;
    className?: string;
    size?: "large" | "medium";
    variant?: "outlined" | "filled";
    interaction?: "hover" | "focus";
    disabled?: boolean;
    error?: boolean;
};

/**
 * 'interaction' allows Component to trigger 'hover' or 'focus' CSS programatically.
 * For example, Select component isn't based on primitive <select> element, so it can't trigger :focus pseudo-class through css.
 * Instead, it can trigger :focus css through 'interaction' prop when it shows Menu dropdown.
 */
export const BaseFormBox = forwardRef<
    HTMLDivElement,
    Omit<HTMLProps<HTMLDivElement>, "size"> & BaseFormBoxProps
>(
    (
        {
            size = "medium",
            variant = "outlined",
            interaction,
            disabled,
            error,
            className,
            ...props
        },
        ref
    ) => {
        const status = useMemo(
            () => (error ? "error" : disabled ? "disabled" : "default"),
            [error, disabled]
        );

        // Size classes
        const sizeClasses = {
            large: "py-3 px-5",
            medium: "py-1 px-2"
        }[size];

        // Base classes
        const baseClasses = "rounded-medium transition-all";

        // Variant-specific classes
        const variantClasses = {
            outlined: cn(
                // Default state
                "bg-common-white-main shadow-[inset_0_0_0_1px_var(--grey-400)]",
                // Hover state (when data-use-interaction="true" and not focused)
                "data-[use-interaction=true]:hover:not-focus:not-focus-within:not-[data-interaction=focus] data-[use-interaction=true]:hover:shadow-[inset_0_0_0_1px_var(--primary-main)]",
                // Programmatic hover (data-interaction="hover")
                "data-[interaction=hover]:shadow-[inset_0_0_0_1px_var(--primary-main)]",
                // Focus state
                "data-[use-interaction=true]:focus:shadow-[inset_0_0_0_1px_var(--primary-main)] data-[use-interaction=true]:focus:outline data-[use-interaction=true]:focus:outline-[1.3px] data-[use-interaction=true]:focus:outline-primary-dark",
                "data-[use-interaction=true]:focus-within:shadow-[inset_0_0_0_1px_var(--primary-main)] data-[use-interaction=true]:focus-within:outline data-[use-interaction=true]:focus-within:outline-[1.3px] data-[use-interaction=true]:focus-within:outline-primary-dark",
                // Programmatic focus (data-interaction="focus")
                "data-[interaction=focus]:shadow-[inset_0_0_0_1px_var(--primary-main)] data-[interaction=focus]:outline data-[interaction=focus]:outline-[1.3px] data-[interaction=focus]:outline-primary-dark",
                // Disabled state
                "data-[disabled=true]:bg-common-white-main data-[disabled=true]:text-text-disabled data-[disabled=true]:shadow-[inset_0_0_0_1px_var(--action-disabled)] data-[disabled=true]:*:cursor-not-allowed",
                // Error state
                "data-[error=true]:bg-common-white-main data-[error=true]:shadow-[inset_0_0_0_1px_var(--error-main)] data-[error=true]:outline data-[error=true]:outline-[1.3px] data-[error=true]:outline-error-main"
            ),
            filled: cn(
                // Default state
                "bg-grey-100 shadow-[inset_0_0_0_1px_transparent]",
                // Hover state
                "data-[use-interaction=true]:hover:not-focus:not-focus-within:not-[data-interaction=focus] data-[use-interaction=true]:hover:shadow-[inset_0_0_0_1px_var(--grey-700)]",
                "data-[interaction=hover]:shadow-[inset_0_0_0_1px_var(--grey-700)]",
                // Focus state
                "data-[use-interaction=true]:focus:shadow-[inset_0_0_0_1px_var(--grey-700)] data-[use-interaction=true]:focus:outline data-[use-interaction=true]:focus:outline-[1.3px] data-[use-interaction=true]:focus:outline-grey-700",
                "data-[use-interaction=true]:focus-within:shadow-[inset_0_0_0_1px_var(--grey-700)] data-[use-interaction=true]:focus-within:outline data-[use-interaction=true]:focus-within:outline-[1.3px] data-[use-interaction=true]:focus-within:outline-grey-700",
                "data-[interaction=focus]:shadow-[inset_0_0_0_1px_var(--grey-700)] data-[interaction=focus]:outline data-[interaction=focus]:outline-[1.3px] data-[interaction=focus]:outline-grey-700",
                // Disabled state
                "data-[disabled=true]:bg-action-disabled-background data-[disabled=true]:text-text-disabled data-[disabled=true]:shadow-[inset_0_0_0_1px_var(--grey-400)] data-[disabled=true]:*:cursor-not-allowed",
                // Error state
                "data-[error=true]:bg-error-selected data-[error=true]:shadow-[inset_0_0_0_1px_var(--error-main)] data-[error=true]:outline data-[error=true]:outline-[1.3px] data-[error=true]:outline-error-main"
            )
        }[variant];

        return (
            <div
                ref={ref}
                className={cn(
                    baseClasses,
                    sizeClasses,
                    variantClasses,
                    className
                )}
                data-use-interaction={status === "default"}
                data-interaction={interaction}
                data-disabled={disabled}
                data-error={!!error}
                {...props}
            />
        );
    }
);

BaseFormBox.displayName = "BaseFormBox";
